/**
 * My Docs -> Share Link modal.
 * Spec: Sendlinx_My_Docs_Share_Link_Developer_Specification.docx (v1.0, 23 Oct 2026) + approved mockup.
 *
 * The user has already created a link for My Docs files (NewLinkModal); this modal emails it.
 * Sections, in the spec's order (§3): header, FAR active banner, Recipients (+ read-only From),
 * Security options, Message, File summary, FAR check, footer (Send Link / Cancel).
 *
 * - ~750 px wide on desktop; sections stack; the body scrolls and the footer stays reachable (§11).
 * - Recipients: tokens (TAB / ENTER / paste / comma), shared Address Book (groups expand to
 *   addresses), case-insensitive de-duplication (§4). The contact source never grants FAR permission.
 * - FAR: the server validates (POST user/link/share/validate, same engine as the Send page) and the
 *   state matrix of §7 drives the FAR check and the Send Link button. Authorization uses the same
 *   exception review as the Send page; the server re-checks everything again on send.
 * - Send: idempotency key, server-sanitized message, and the result is shown as sent (§10).
 * - ESC / Cancel ask before discarding typed content (§11).
 */
import React from 'react'
import withStyles from "@material-ui/core/styles/withStyles";
import PropTypes from "prop-types";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";
import RichTextEditor from 'react-rte';
import { RichUtils } from 'draft-js';
import InputAdornment from "@material-ui/core/InputAdornment";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Check from "@material-ui/icons/Check";
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CloseIcon from '@material-ui/icons/Close';
import LinkIcon from "@material-ui/icons/Link";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import SecurityIcon from "@material-ui/icons/Security";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import DescriptionOutlined from "@material-ui/icons/DescriptionOutlined";
import InsertDriveFileOutlined from "@material-ui/icons/InsertDriveFileOutlined";
import ContactsOutlined from "@material-ui/icons/ContactsOutlined";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import BlockIcon from "@material-ui/icons/Block";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import MaterialIcon from "@mdi/react";
import { mdiAccountGroup } from '@mdi/js';
import { helper } from "../../../assets/js/utils/Element";
import Cookie from "../../../assets/js/utils/Cookie";
import CustomInput from "../CustomInput/CustomInput";
import Button from "../CustomButtons/Button.jsx";
import DateTimePicker from "../CustomInput/DateTimePicker";
import LoaderComponent from "../Loader";
import FarApi from "../../../assets/js/utils/far/farApi";
import { evaluateSend, buildFarSendPayload, describeExclusion, FAR_DEFAULT_SETTINGS, FAR_ENFORCEMENT } from "../../../assets/js/utils/far/farEngine";
import { isFarAdmin } from "./Far/farAccess";
import FarStatusBanner from "./Far/FarStatusBanner";
import FarExceptionModal from "./Far/FarExceptionModal";
import FarRuleDetails from "./Far/FarRuleDetails";
import AddressBookModal from "./Contacts/AddressBookModal.jsx";
import ShareLinkApi, { newIdempotencyKey } from "../../../assets/js/utils/share/shareLinkApi";
import shareLinkModalStyle from "../../../assets/jss/user/shareLinkModalStyle.jsx";
import sendPageStyle from "../../../assets/jss/user/far/sendPageStyle.jsx";
import shareLinkLayoutStyle from "../../../assets/jss/user/shareLinkLayoutStyle.jsx";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const lower = (v) => String(v || "").trim().toLowerCase();
const splitEmails = (text) => String(text || "").split(/[;,\s]+/).map(e => e.trim()).filter(Boolean);
const VALIDATE_DELAY = 450;
/** States of spec §7 that allow Send Link (subject to the other fields). */
const SENDABLE = ["passed", "no_rule", "off"];
const SHARE_CHECKS = ["File location and name", "Recipient email addresses", "Contact groups (expanded)", "Allowed / restricted recipients", "File prefixes and suffixes", "Rule exceptions (if any)"];

/** "/My Docs/Customers/ABC" -> "My Docs / Customers / ABC" */
const formatLocation = (path) => String(path || "").split("/").map(p => p.trim()).filter(Boolean).join(" / ");

/**
 * API errors can carry a string, or Laravel validation errors as an object
 * ({ "recipients.0": "The x@y.com must be a valid email address." }). Always render text.
 */
const errorText = (value) => {
    if(value === null || value === undefined){ return ""; }
    if(typeof(value) === "string"){ return value; }
    if(Array.isArray(value)){ return value.map(errorText).filter(Boolean).join(" "); }
    if(typeof(value) === "object"){ return Object.keys(value).map(k => errorText(value[k])).filter(Boolean).join(" "); }
    return String(value);
};

class ShareLinkModal extends React.Component {
    constructor(props){
        super(props);
        this.store = this.props.store;
        let yourEmail = Cookie.read("sendlinx_your_email");
        if(!yourEmail || yourEmail === "null"){
            yourEmail = "";
        }
        const { authorized, user } = this.store.getState();
        if(authorized){
            yourEmail = user.email;
        }
        this.initial = {
            linkPassword: typeof(this.props.linkPassword) === "string" ? this.props.linkPassword : "",
            expire: this.props.expire ? this.props.expire : null,
            requireVerification: !!this.props.requireVerification
        };
        this.state = {
            yourEmail: yourEmail,
            linkPassword: this.initial.linkPassword,
            viewPassword: false,
            expire: this.initial.expire,
            expirePreset: null,
            requireVerification: this.initial.requireVerification,
            // Permitted emails chosen when the link was created with email verification.
            recipients: (this.props.initialRecipients || []).map(lower).filter((e, i, all) => emailRegex.test(e) && all.indexOf(e) === i),
            emailTo: "",
            emailError: null,
            message: RichTextEditor.createEmptyValue(),
            context: null,
            contextError: null,
            far: { loading: false, error: null, settings: FAR_DEFAULT_SETTINGS, rules: [], canOverride: false, isAdmin: false, loaded: false },
            validation: { state: "pending", result: null, message: "" },
            farExceptions: {},
            farActions: [],
            farModal: null,
            rulesOpen: false,
            addressBookModal: false,
            confirmClose: false,
            saving: false,
            sendError: null,
            sent: null
        };
        this.validateTimer = null;
        this.validateRequest = 0;
        this.idempotency = { key: null, signature: null };
        this.onFarRemoveRecipients = this.onFarRemoveRecipients.bind(this);
        this.onFarResolve = this.onFarResolve.bind(this);
        this.onAddressBookAdd = this.onAddressBookAdd.bind(this);
        this.requestClose = this.requestClose.bind(this);
        this.runValidation = this.runValidation.bind(this);
        this.loadContext = this.loadContext.bind(this);
    }
    componentDidMount(){
        this.loadContext();
    }
    componentDidUpdate(prevProps, prevState){
        if(prevState.recipients !== this.state.recipients || prevState.farExceptions !== this.state.farExceptions || prevState.context !== this.state.context){
            this.scheduleValidation();
        }
    }
    componentWillUnmount(){
        clearTimeout(this.validateTimer);
        [this.contextSource, this.farSource, this.validateSource, this.sendSource].forEach(source => {
            if(source){ source.cancel("Request Cancelled"); }
        });
    }

    /* ------------------------------ loading ------------------------------ */
    loadContext(){
        if(this.contextSource){ this.contextSource.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        this.contextSource = source;
        this.setState({ contextError: null });
        ShareLinkApi.context(this.props.linkHash, source).then(context => {
            this.setState({ context });
            if(context.farEnabled){
                this.loadFarConfig();
            }
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            this.setState({ contextError: errorText(err && err.message) || "The link details could not be loaded." });
        });
    }
    /** Rules for the read-only View Rules list and the authorization review (the server decides). */
    loadFarConfig(){
        const { user } = this.store.getState();
        if(this.farSource){ this.farSource.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        this.farSource = source;
        this.setState({ far: Object.assign({}, this.state.far, { loading: true, error: null }) });
        FarApi.getConfig(source).then(config => {
            const settings = config.settings;
            const adminOnly = settings.enforcement === FAR_ENFORCEMENT.BLOCK || settings.override_permission === "admins";
            this.setState({ far: { loading: false, error: null, loaded: true, settings, rules: config.rules,
                canOverride: !!config.canOverride && (adminOnly ? isFarAdmin(user) : true),
                isAdmin: config.isAdmin !== undefined ? !!config.isAdmin : isFarAdmin(user) } });
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            this.setState({ far: Object.assign({}, this.state.far, { loading: false, error: "The rules could not be loaded." }) });
        });
    }

    /* ------------------------------ FAR validation (server) ------------------------------ */
    getFarItems(){
        const { context } = this.state;
        if(!context){ return []; }
        return [{ source: "mydocs", id: "link", files: (context.files || []).map(f => ({
            name: f.name, path: (f.folder ? f.folder+"/" : "")+f.name, folder: f.folder || null, size: f.size || 0
        })) }];
    }
    /** Client evaluation: only for the authorization review and the rules list. Never decides sending. */
    getClientEvaluation(recipients = null){
        const { far, farExceptions } = this.state;
        if(!far.loaded){ return null; }
        return evaluateSend({ items: this.getFarItems(), recipients: recipients === null ? this.state.recipients : recipients,
            rules: far.rules, settings: far.settings, transferType: "email", exceptions: farExceptions,
            isAdmin: !!far.isAdmin, canOverride: !!far.canOverride });
    }
    getFarPayload(){
        const evaluation = this.getClientEvaluation();
        return evaluation ? buildFarSendPayload(evaluation, this.state.farActions) : null;
    }
    scheduleValidation(){
        clearTimeout(this.validateTimer);
        const { context, recipients } = this.state;
        if(!context){ return; }
        if(!context.farEnabled){
            this.setState({ validation: { state: "off", result: null, message: "" } });
            return;
        }
        if(recipients.length <= 0){
            this.validateRequest++;
            this.setState({ validation: { state: "pending", result: null, message: "" } });
            return;
        }
        this.setState({ validation: Object.assign({}, this.state.validation, { state: "checking" }) });
        this.validateTimer = setTimeout(this.runValidation, VALIDATE_DELAY);
    }
    runValidation(){
        const id = ++this.validateRequest;
        if(this.validateSource){ this.validateSource.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        this.validateSource = source;
        this.setState({ validation: Object.assign({}, this.state.validation, { state: "checking" }) });
        ShareLinkApi.validate({ hash: this.props.linkHash, recipients: this.state.recipients, farEvaluation: this.getFarPayload() }, source).then(result => {
            if(id !== this.validateRequest){ return; }
            this.setState({ validation: { state: result.status || "error", result, message: result.safeUserMessage || "" } });
        }).catch(err => {
            if(id !== this.validateRequest || (err && err.message === "Request Cancelled")){ return; }
            // Fail closed (§5): Send Link stays disabled until the check succeeds.
            this.setState({ validation: { state: "error", result: null, message: errorText(err && err.message) || "Unable to verify the recipients." } });
        });
    }
    getRecipientStatus(email){
        const { validation } = this.state;
        const rows = validation.result && validation.state !== "checking" ? validation.result.perRecipient || [] : [];
        const row = rows.find(r => r.email === lower(email));
        if(!row){ return null; }
        return row.status === "excluded" ? "blocked" : row.status === "not_permitted" ? "bad" : row.status === "exception" ? "warn" : row.status === "permitted" ? "ok" : null;
    }
    /** Server's reason for an excluded recipient ("Blocked by FAR exclusion: ..."), or null. */
    getRecipientExclusionText(email){
        const { validation } = this.state;
        const rows = validation.result && validation.state !== "checking" ? validation.result.perRecipient || [] : [];
        const row = rows.find(r => r.email === lower(email));
        return row && row.status === "excluded" ? describeExclusion(row.exclusion) : null;
    }
    blockedRecipients(){
        const result = this.state.validation.result;
        return result ? (result.perRecipient || []).filter(r => r.status === "not_permitted" || r.status === "excluded") : [];
    }
    openReview(){
        const evaluation = this.getClientEvaluation();
        if(evaluation && evaluation.unresolvedPairs.length > 0){
            // Warn only never covers a block / admin-approval exclusion (requiresAcknowledgement is false then).
            this.setState({ farModal: evaluation.requiresAcknowledgement ? "acknowledge" : "resolve" });
        }
    }
    onFarRemoveRecipients(toRemove){
        const remove = toRemove.map(lower);
        const evaluation = this.getClientEvaluation();
        const now = new Date().toISOString();
        const actions = this.state.farActions.slice();
        if(evaluation){
            evaluation.unresolvedPairs.filter(p => remove.includes(p.recipient)).forEach(p => {
                actions.push({ action: "recipient_removed", recipient: p.recipient, file: p.fileName, ruleId: p.ruleId, at: now });
            });
        }
        this.setState({ recipients: this.state.recipients.filter(r => !remove.includes(lower(r))), farActions: actions, farModal: null });
    }
    onFarResolve({ action, reason }){
        const evaluation = this.getClientEvaluation();
        if(!evaluation){
            this.setState({ farModal: null });
            return;
        }
        const { user } = this.store.getState();
        const now = new Date().toISOString();
        const exceptions = Object.assign({}, this.state.farExceptions);
        const actions = this.state.farActions.slice();
        const auditAction = action === "authorized" ? "exception_authorized" : action === "ignored" ? "ignored" : "acknowledged";
        evaluation.unresolvedPairs.filter(pair => pair.overridable).forEach(pair => {
            exceptions[pair.key] = { action: action, reason: reason, at: now, by: user ? user.email : "" };
            actions.push({ action: auditAction, recipient: pair.recipient, file: pair.fileName, ruleId: pair.ruleId, reason: reason, at: now });
        });
        // The server re-validates with the approval before Send Link is enabled.
        this.setState({ farExceptions: exceptions, farActions: actions, farModal: null });
    }

    /* ------------------------------ recipients ------------------------------ */
    commitTyped(){
        const typed = splitEmails(this.state.emailTo);
        if(typed.length <= 0){
            return;
        }
        const recipients = this.state.recipients.slice();
        typed.filter(e => emailRegex.test(e)).forEach(email => {
            if(!recipients.includes(lower(email))){
                recipients.push(lower(email));
            }
        });
        const invalid = typed.filter(e => !emailRegex.test(e));
        this.setState({
            recipients: recipients.length !== this.state.recipients.length ? recipients : this.state.recipients,
            emailTo: invalid.join(", "),
            emailError: invalid.length > 0 ? "Check the address"+(invalid.length > 1 ? "es" : "")+": "+invalid.join(", ") : null
        });
    }
    onEmailKeyDown(event){
        if(event.key === "Enter" || event.key === "Tab" || event.key === "," || event.key === ";"){
            if(this.state.emailTo.trim().length > 0){
                event.preventDefault();
                this.commitTyped();
            }
        }
    }
    removeRecipient(e, email){
        e.preventDefault();
        this.setState({ recipients: this.state.recipients.filter(r => r !== email) });
    }
    editRecipient(e, email){
        e.preventDefault();
        const typed = splitEmails(this.state.emailTo).filter(t => lower(t) !== email);
        this.setState({ recipients: this.state.recipients.filter(r => r !== email), emailTo: typed.concat([email]).join(", ") });
    }
    onAddressBook(open){
        if(open){
            this.commitTyped();
        }
        this.setState({ addressBookModal: open });
    }
    onAddressBookAdd(emails){
        const recipients = this.state.recipients.slice();
        (emails || []).forEach(email => {
            const value = lower(email);
            if(value && emailRegex.test(value) && !recipients.includes(value)){
                recipients.push(value);
            }
        });
        this.setState({ recipients, addressBookModal: false, emailError: null });
    }

    /* ------------------------------ send ------------------------------ */
    expireInvalid(){
        const { expire } = this.state;
        if(!expire){ return false; }
        const d = new Date(expire);
        return isNaN(d.getTime()) || d.getTime() <= Date.now();
    }
    getSendDisabledReason(){
        const { saving, context, contextError, recipients, emailTo, validation } = this.state;
        if(saving){ return "Sending…"; }
        if(contextError){ return "The link details could not be loaded."; }
        if(!context){ return "Loading the link details…"; }
        if(recipients.length <= 0){ return "Add at least one recipient."; }
        if(emailTo.trim().length > 0){ return "Press ENTER to add the address you typed, or clear it."; }
        if(this.expireInvalid()){ return "Choose an expiry date in the future."; }
        switch(validation.state){
            case "checking": return "Checking applicable FAR rules…";
            case "review": return "Authorization is required before this link can be sent.";
            case "blocked": return validation.result && validation.result.excludedCount > 0 ? "Remove the recipients blocked by a FAR exclusion." : "Remove the recipients who are not permitted.";
            case "error": return "File Attachment Rules could not be checked. Retry the check.";
            case "pending": return "Checking applicable FAR rules…";
            default: return SENDABLE.includes(validation.state) ? null : "Checking applicable FAR rules…";
        }
    }
    shareLink(){
        if(this.getSendDisabledReason() !== null){
            return;
        }
        const { linkPassword, expire, requireVerification, recipients } = this.state;
        const data = {
            hash: this.props.linkHash,
            recipients: recipients,
            message: this.state.message.toString("html"),
            requireVerification: requireVerification ? true : false
        };
        if(linkPassword.length > 0){ data.linkPassword = linkPassword; }
        if(expire){ data.expire = expire; }
        const farEvaluation = this.getFarPayload();
        if(farEvaluation){ data.farEvaluation = farEvaluation; }
        // Same content -> same key, so a retry after a dropped response is not sent twice.
        const signature = JSON.stringify(data);
        if(this.idempotency.signature !== signature){
            this.idempotency = { key: newIdempotencyKey(), signature };
        }
        data.idempotencyKey = this.idempotency.key;
        const source = axios.CancelToken.source();
        this.sendSource = source;
        this.setState({ saving: true, sendError: null, confirmClose: false });
        ShareLinkApi.send(data, source).then(result => {
            this.setState({ saving: false, sent: result || { message: "Link sent." } });
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            const violations = err && err.far && Array.isArray(err.far.violations) ? err.far.violations : [];
            let message = errorText(err && err.message) || "The link could not be sent. Please try again.";
            if(err && typeof(err) === "object" && !err.message && Object.keys(err).length > 0){
                message = errorText(err) || message;
            }
            this.setState({ saving: false, sendError: { message, violations } });
            if(violations.length > 0){
                this.scheduleValidation();
            }
        });
    }

    /* ------------------------------ closing ------------------------------ */
    isDirty(){
        const s = this.state;
        return s.recipients.length > 0 || s.emailTo.trim().length > 0 || s.message.getEditorState().getCurrentContent().hasText()
            || s.linkPassword !== this.initial.linkPassword || s.expire !== this.initial.expire || s.requireVerification !== this.initial.requireVerification;
    }
    requestClose(){
        if(this.state.saving){
            return;
        }
        if(this.state.sent){
            this.props.onSuccess();
            return;
        }
        if(this.isDirty() && !this.state.confirmClose){
            this.setState({ confirmClose: true });
            return;
        }
        this.props.onClose();
    }

    /* ------------------------------ editor ------------------------------ */
    getConfig(){
        return {
            display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'HISTORY_BUTTONS'],
            INLINE_STYLE_BUTTONS: [
                {label: 'Bold', style: 'BOLD', className: 'custom-css-class'},
                {label: 'Italic', style: 'ITALIC'},
                {label: 'Underline', style: 'UNDERLINE'}
            ],
            BLOCK_TYPE_BUTTONS: [
                {label: 'UL', style: 'unordered-list-item'},
                {label: 'OL', style: 'ordered-list-item'}
            ]
        };
    }
    getCustomControls(){
        const { classes } = this.props;
        const { message } = this.state;
        return [
            <div className="ButtonGroup__root___3lEAn" key={1}>
                <span className="Dropdown__root___3ALmx">
                    <label className={classes.hiddenLabel} htmlFor="slm_heading_type">Heading</label>
                    <select id="slm_heading_type" onChange={(e) => {
                        this.setState({ message: message.setEditorState(RichUtils.toggleBlockType(message.getEditorState(), e.target.value)) });
                    }}>
                        <option value="unstyled">Normal</option>
                        <option value="header-one">Heading Large</option>
                        <option value="header-two">Heading Medium</option>
                        <option value="header-three">Heading Small</option>
                    </select>
                    <span className="Dropdown__value___34Py9"> </span>
                </span>
            </div>
        ];
    }

    /* ------------------------------ render ------------------------------ */
    renderFileSummary(){
        const { classes, items } = this.props;
        const { context, contextError } = this.state;
        const files = context ? context.files || [] : [];
        const fallbackThumb = (items || []).map(i => i && i.thumbnails ? (i.thumbnails.medium || i.thumbnails.small) : null).find(t => typeof(t) === "string");
        let body;
        if(contextError){
            body = (
                <div className={classes.slmState+" "+classes.slmState_danger} role="alert">
                    <ErrorOutline aria-hidden="true" />
                    <div><strong>The file details could not be loaded.</strong><span>{contextError}</span></div>
                    <button type="button" className={classes.slmTextButton} onClick={this.loadContext}>Retry</button>
                </div>
            );
        }else if(!context){
            body = <LoaderComponent color="custom" align="center" />;
        }else if(files.length === 1){
            const f = files[0];
            const thumb = f.thumbnail || (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(f.extension) ? fallbackThumb : null);
            body = (
                <div className={classes.slmFileSingle}>
                    { thumb ? <img className={classes.slmThumb} src={thumb} alt="" /> : <span className={classes.slmThumbIcon} aria-hidden="true"><InsertDriveFileOutlined /></span> }
                    <div className={classes.slmFileText}>
                        <p className={classes.slmFileName} title={f.name}>{f.name}</p>
                        <p className={classes.slmFileMeta}>{helper.getFormatedSize(f.size)}{f.extension ? " · "+f.extension.toUpperCase() : ""}</p>
                        { f.folder ? <p className={classes.slmFileMeta}>Location: {formatLocation(f.folder)}</p> : null }
                    </div>
                </div>
            );
        }else if(files.length > 1){
            body = (
                <ul className={classes.slmFileList}>
                    {
                        files.slice(0, 4).map(f => (
                            <li key={f.id} className={classes.slmFileRow}>
                                <span className={classes.slmThumbSmall} aria-hidden="true"><InsertDriveFileOutlined /></span>
                                <span className={classes.slmFileText}>
                                    <span className={classes.slmFileName} title={f.name}>{f.name}</span>
                                    <span className={classes.slmFileMeta}>{helper.getFormatedSize(f.size)}{f.extension ? " · "+f.extension.toUpperCase() : ""}{f.folder ? " · "+formatLocation(f.folder) : ""}</span>
                                </span>
                            </li>
                        ))
                    }
                    { files.length > 4 ? <li className={classes.slmFileMeta}>+{files.length - 4} more · {helper.getFormatedSize(context.totalSize)} in total</li> : null }
                </ul>
            );
        }else{
            body = <p className={classes.slmFileMeta}>This link has no files that can be shared.</p>;
        }
        return (
            <section className={classes.spCard} aria-labelledby="slm-file-title">
                <h3 className={classes.spCardTitle} id="slm-file-title"><DescriptionOutlined aria-hidden="true" />{files.length > 1 ? "Files ("+files.length+")" : "File"}</h3>
                {body}
            </section>
        );
    }
    renderFarCheck(){
        const { classes } = this.props;
        const { context, validation, far } = this.state;
        const farOn = !!(context && context.farEnabled);
        const result = validation.result;
        const blocked = this.blockedRecipients();
        let tone = "neutral", Icon = InfoOutlined, title = "", text = "", action = null;
        switch(validation.state){
            case "off":
                title = "File Attachment Rules are off";
                text = "Your organization has not turned on File Attachment Rules. Normal sharing rules apply.";
                break;
            case "checking":
                Icon = null; title = "Checking applicable FAR rules…"; text = "The file and recipients are being checked on the server.";
                break;
            case "no_rule":
                title = "No matching restrictions";
                text = (result && result.safeUserMessage) || "No File Attachment Rule restricts this file. Normal sharing rules apply.";
                break;
            case "passed":
                tone = "success"; Icon = CheckCircle; title = "All recipients permitted";
                text = (result && result.safeUserMessage) || "All recipients are permitted.";
                break;
            case "review":
                tone = "warning"; Icon = ReportProblemOutlined; title = "Authorization required";
                text = (result && result.safeUserMessage) || "Authorization is required before this link can be sent.";
                action = far.loaded ? (
                    <button type="button" className={classes.spException} onClick={() => this.openReview()}>
                        {far.settings.enforcement === FAR_ENFORCEMENT.WARN_ONLY && !(result && result.excludedCount > 0) ? "Review and confirm" : "Review recipients"}
                    </button>
                ) : null;
                break;
            case "blocked":
                tone = "danger"; Icon = result && result.excludedCount > 0 ? BlockIcon : ErrorOutline;
                title = result && result.excludedCount > 0 ? "Recipient(s) blocked by FAR exclusion" : "Recipient(s) not permitted";
                text = (result && result.safeUserMessage) || "Some recipients are not permitted to receive this file.";
                action = <button type="button" className={classes.spException} onClick={() => this.onFarRemoveRecipients(blocked.map(r => r.email))}>{result && result.excludedCount > 0 ? "Remove blocked recipients" : "Remove recipients who are not permitted"}</button>;
                break;
            case "error":
                tone = "danger"; Icon = ErrorOutline; title = "Unable to verify";
                text = (validation.message || "File Attachment Rules could not be checked.")+" Nothing has been sent.";
                action = <button type="button" className={classes.spException} onClick={this.runValidation}>Retry</button>;
                break;
            default:
                title = "Ready to validate"; text = "Add recipients to validate.";
        }
        return (
            <section className={classes.spCard} aria-labelledby="slm-far-title">
                <h3 className={classes.spCardTitle} id="slm-far-title"><VerifiedUser aria-hidden="true" className={classes.slmShield} />File Attachment Rules Check</h3>
                {
                    farOn || validation.state !== "off" ?
                        <p className={classes.slmIntro}>The selected file and recipients will be validated against your File Attachment Rules before sending.</p>
                    : null
                }
                <div className={classes.slmState+" "+classes["slmState_"+tone]} role="status" aria-live="polite" aria-atomic="true">
                    { Icon ? <Icon aria-hidden="true" /> : <LoaderComponent color="custom" saving={true} /> }
                    <div><strong>{title}</strong><span>{text}</span></div>
                </div>
                {
                    blocked.length > 0 && (validation.state === "review" || validation.state === "blocked") ?
                        <ul className={classes.slmIssues} aria-label="Recipients that need attention">
                            {
                                blocked.map(r => (
                                    <li key={r.email}>
                                        {
                                            r.status === "excluded" ?
                                                <><BlockIcon aria-hidden="true" /><span><strong>{r.email}</strong> — {describeExclusion(r.exclusion)}
                                                    { r.exclusion && r.exclusion.action === "block" ? " It can't be overridden; remove this recipient." : r.exclusion && r.exclusion.action === "admin_approval" ? " Requires administrator authorization." : "" }</span></>
                                            :
                                                <><ErrorOutline aria-hidden="true" /><span><strong>{r.email}</strong>
                                                { r.allowed && r.allowed.length > 0 ? <> is not permitted. Permitted: {r.allowed.join(", ")}</> : " is not permitted to receive this file." }</span></>
                                        }
                                    </li>
                                ))
                            }
                        </ul>
                    : null
                }
                { action ? <div className={classes.slmStateActions}>{action}</div> : null }
                {
                    validation.state === "pending" && farOn ?
                        <>
                            <hr className={classes.slmDivider} />
                            <h4 className={classes.slmChecksTitle}>What will be checked?</h4>
                            <ul className={classes.slmChecks}>
                                { SHARE_CHECKS.map(item => <li key={item}><CheckCircle aria-hidden="true" />{item}</li>) }
                            </ul>
                        </>
                    : null
                }
                {
                    farOn ?
                        <div className={classes.slmState+" "+classes.slmState_neutral}>
                            <InfoOutlined aria-hidden="true" /><div><span>If a recipient does not meet the rules, you'll be notified before sending.</span></div>
                        </div>
                    : null
                }
            </section>
        );
    }
    renderRulesDialog(){
        const { classes } = this.props;
        const { far, rulesOpen } = this.state;
        if(!rulesOpen){ return null; }
        const evaluation = this.getClientEvaluation([]);
        const matches = [];
        if(evaluation){
            evaluation.matchedFiles.forEach(file => file.matches.forEach(match => {
                if(!matches.some(m => m.match.rule.id === match.rule.id)){
                    matches.push({ file, match });
                }
            }));
        }
        return (
            <Dialog open={true} onClose={() => this.setState({ rulesOpen: false })} classes={{ paper: classes.slmRulesPaper }} aria-labelledby="slm-rules-title" maxWidth={false}>
                <div className={classes.slmRulesHeader}>
                    <h3 className={classes.slmRulesTitle} id="slm-rules-title">Rules that apply to this file</h3>
                    <button type="button" className={classes.slmClose} onClick={() => this.setState({ rulesOpen: false })} aria-label="Close rules"><CloseIcon /></button>
                </div>
                <div className={classes.slmRulesBody}>
                    <p className={classes.slmIntro}>Read only. Rules are managed by your organization's administrators.</p>
                    {
                        far.loading ? <LoaderComponent color="custom" align="center" />
                        : far.error ? <p className={classes.slmFileMeta}>{far.error} <button type="button" className={classes.slmTextButton} onClick={() => this.loadFarConfig()}>Retry</button></p>
                        : matches.length === 0 ? <p className={classes.slmFileMeta}>No File Attachment Rule applies to this file.</p>
                        : matches.map(({ file, match }) => <FarRuleDetails key={match.rule.id} rule={match.rule} evaluation={match.evaluation} settings={far.settings} file={file} />)
                    }
                </div>
            </Dialog>
        );
    }
    renderSent(){
        const { classes } = this.props;
        const { sent } = this.state;
        return (
            <div className={classes.slmSent} role="status" aria-live="polite">
                <CheckCircle aria-hidden="true" />
                <h3>Link sent</h3>
                <p>{sent.message || "The link was emailed to your recipients."}</p>
                {
                    sent.verificationRequired ?
                        <p className={classes.slmFileMeta}>
                            Recipients must verify their email address before they can open the file.
                            { sent.verificationForced ? " Email verification was turned on because a File Attachment Rule applies to this file." : "" }
                        </p>
                    : null
                }
            </div>
        );
    }
    render(){
        const { authorized } = this.store.getState();
        const { classes } = this.props;
        const { linkPassword, recipients, emailTo, emailError, yourEmail, message, expire, expirePreset, saving, viewPassword,
            requireVerification, context, farModal, addressBookModal, confirmClose, sendError, sent, validation } = this.state;
        const files = context ? context.files || [] : [];
        const several = files.length > 1;
        const disabledReason = this.getSendDisabledReason();
        const reviewEvaluation = farModal ? this.getClientEvaluation() : null;

        return (
            <Dialog
                classes={{ paper: classes.slmPaper }}
                open={this.props.open}
                maxWidth={false}
                scroll="paper"
                aria-labelledby="shareLinkModal-title"
                aria-describedby="shareLinkModal-subtitle"
                onClose={this.requestClose}
            >
                <div className={classes.slmHeader}>
                    <LinkIcon className={classes.slmHeaderIcon} aria-hidden="true" />
                    <div className={classes.slmHeaderText}>
                        <h2 className={classes.slmTitle} id="shareLinkModal-title">Share Link</h2>
                        <p className={classes.slmSubtitle} id="shareLinkModal-subtitle">{several ? "Share these files securely with others." : "Share this file securely with others."}</p>
                    </div>
                    <button type="button" className={classes.slmClose} onClick={this.requestClose} disabled={saving} aria-label="Close"><CloseIcon /></button>
                </div>
                <div className={classes.slmBody+" "+classes.homepageFormFields+" uploader-fields"}>
                    {
                        sent ? this.renderSent() : <>
                        {
                            sendError ?
                                <div className={classes.slmState+" "+classes.slmState_danger} role="alert">
                                    <ErrorOutline aria-hidden="true" />
                                    <div>
                                        <strong>{sendError.message}</strong>
                                        { sendError.violations.length > 0 ? <span>{sendError.violations.slice(0, 5).map(v => v.recipient).filter((v, i, a) => a.indexOf(v) === i).join(", ")} cannot receive this file.</span> : <span>Nothing was sent.</span> }
                                    </div>
                                </div>
                            : null
                        }
                        {
                            context && context.farEnabled ?
                                <FarStatusBanner variant="send"
                                    description={several ? "Your files and recipients will be checked before sending." : "Your file and recipients will be checked before sending."}
                                    onViewRules={() => this.setState({ rulesOpen: true })} />
                            : null
                        }
                        <section className={classes.spCard} aria-labelledby="slm-recipients-title">
                            <h3 className={classes.spCardTitle} id="slm-recipients-title"><PeopleOutline aria-hidden="true" />Recipients</h3>
                            <div className="formElement">
                                <label className={classes.spLabel} htmlFor="slm-email">To</label>
                                {
                                    recipients.length > 0 ?
                                        <div className={"react-tagsinput "+classes.tagsInput}>
                                            <div className="users-icon"><MaterialIcon path={mdiAccountGroup} /></div>
                                            <div className="users-lists" role="list" aria-label="Recipients">
                                                {
                                                    recipients.map(email => {
                                                        const status = this.getRecipientStatus(email);
                                                        const exclusionText = status === "blocked" ? this.getRecipientExclusionText(email) : null;
                                                        return (
                                                            <span role="listitem" className={"react-tagsinput-tag rose "+(status === "bad" || status === "blocked" ? classes.spChipUnauthorized : "")} key={email} title={exclusionText || undefined}>
                                                                {
                                                                    status === "blocked" ? <BlockIcon className={classes.spChipIcon+" "+classes.spChipBad} aria-label={exclusionText || "Blocked by FAR exclusion"} role="img" />
                                                                    : status === "ok" ? <CheckCircle className={classes.spChipIcon+" "+classes.spChipOk} aria-label="Permitted" role="img" />
                                                                    : status === "bad" ? <ErrorOutline className={classes.spChipIcon+" "+classes.spChipBad} aria-label="Not permitted" role="img" />
                                                                    : status === "warn" ? <ReportProblemOutlined className={classes.spChipIcon+" "+classes.spChipWarn} aria-label="Authorized exception" role="img" />
                                                                    : null
                                                                }
                                                                <span onClick={(e) => this.editRecipient(e, email)}>{email}</span>
                                                                <a href="/" aria-label={"Remove "+email} onClick={(e) => this.removeRecipient(e, email)}>&nbsp;</a>
                                                            </span>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    : null
                                }
                                <div className={classes.spRecipientInputRow}>
                                    <CustomInput
                                        id="slm-email"
                                        error={!!emailError}
                                        formControlProps={{ fullWidth: true, className: classes.slmToField }}
                                        labelText="Enter email addresses or select from your contacts…"
                                        inputProps={{
                                            type: "email",
                                            onChange: (e) => this.setState({ emailTo: e.target.value, emailError: null }),
                                            name: "emailTo",
                                            value: emailTo,
                                            onKeyDown: (e) => this.onEmailKeyDown(e),
                                            onBlur: () => this.commitTyped(),
                                            onPaste: () => setTimeout(() => this.commitTyped(), 0),
                                            "aria-describedby": "slm-recipients-help"+(emailError ? " slm-email-error" : ""),
                                            "aria-invalid": emailError ? "true" : "false"
                                        }}
                                    />
                                    {
                                        authorized ?
                                            <button type="button" className={classes.spContactsButton} onClick={() => this.onAddressBook(true)}
                                                title="Select from contacts" aria-label="Select recipients from contacts" aria-haspopup="dialog">
                                                <ContactsOutlined aria-hidden="true" />
                                            </button>
                                        : null
                                    }
                                </div>
                                { emailError ? <p className={classes.slmFieldError} id="slm-email-error" role="alert">{emailError}</p> : null }
                                <p className={"recipients-description "+classes.slmHelp} id="slm-recipients-help">Press TAB or ENTER after each email, or select from your contacts.</p>
                            </div>
                            <div className={"formElement "+classes.spFrom}>
                                <label className={classes.spLabel} htmlFor="slm-from">From <span className={classes.spLabelMuted}>(your account)</span></label>
                                <CustomInput id="slm-from" formControlProps={{ fullWidth: true }} labelText="you@example.com"
                                    inputProps={{ type: "email", name: "yourEmail", value: yourEmail, disabled: true, readOnly: true }} />
                            </div>
                        </section>

                        <section className={classes.spCard} aria-labelledby="slm-security-title">
                            <h3 className={classes.spCardTitle} id="slm-security-title"><SecurityIcon aria-hidden="true" />Security options</h3>
                            <div className={classes.spTwoCol}>
                                <div className="formElement passwordElement">
                                    <label className={classes.spLabel} htmlFor="slm-password">Link password <span className={classes.spLabelMuted}>(optional)</span></label>
                                    <CustomInput
                                        labelText="Enter a password"
                                        id="slm-password"
                                        passwordInput={viewPassword ? false : true}
                                        inputProps={{
                                            value: linkPassword,
                                            name: "linkPassword",
                                            type: "text",
                                            onChange: (e) => this.setState({ linkPassword: e.target.value }),
                                            autoComplete: 'off',
                                            endAdornment: (
                                                <InputAdornment className={classes.inputAdornment+' viewPasswordIcon'} position='end'
                                                    onClick={() => this.setState({ viewPassword: !viewPassword })}
                                                    aria-label={viewPassword ? "Hide password" : "Show password"}>
                                                    { linkPassword ? (viewPassword ? <VisibilityOffIcon className={classes.inputAdornmentIcon}/> : <VisibilityIcon className={classes.inputAdornmentIcon}/>) : <></> }
                                                </InputAdornment>
                                            ),
                                        }}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </div>
                                <div className="formElement dateElement">
                                    <label className={classes.spLabel} htmlFor="slm-expire">Expiry date <span className={classes.spLabelMuted}>(optional)</span></label>
                                    <DateTimePicker
                                        labelText="Select a date"
                                        id="slm-expire"
                                        value={expire}
                                        onChange={(date) => {
                                            const d = new Date(date);
                                            if(!isNaN(d.getTime())){ this.setState({ expire: d.toISOString(), expirePreset: null }); }
                                        }}
                                        formControlProps={{ fullWidth: true }}
                                        disablePast={true}
                                    />
                                    {
                                        expire ?
                                            <Button justIcon color="custom" className="clear-icon" onClick={() => this.setState({ expire: null, expirePreset: null })} aria-label="Clear expiry date">
                                                <svg width="14" height="18" viewBox="0 0 14 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <path d="M11.4082 14.4527C11.6262 14.6707 11.9848 14.6707 12.2027 14.4527C12.4207 14.2348 12.4207 13.8762 12.2027 13.6582L7.54453 9L12.2027 4.3418C12.4207 4.12383 12.4207 3.76523 12.2027 3.54727C11.9848 3.3293 11.6262 3.3293 11.4082 3.54727L6.75 8.20547L2.09531 3.54727C1.87734 3.3293 1.51875 3.3293 1.30078 3.54727C1.08281 3.76523 1.08281 4.12383 1.30078 4.3418L5.95547 9L1.29727 13.6582C1.0793 13.8762 1.0793 14.2348 1.29727 14.4527C1.51523 14.6707 1.87383 14.6707 2.0918 14.4527L6.75 9.79453L11.4082 14.4527Z"/>
                                                </svg>
                                            </Button>
                                        : null
                                    }
                                    <div className={classes.spPresets} role="group" aria-label="Expiry presets">
                                        {
                                            [7, 14, 30].map(days => (
                                                <button type="button" key={days} className={classes.spPreset} aria-pressed={expirePreset === days ? "true" : "false"}
                                                    onClick={() => { const d = new Date(); d.setDate(d.getDate() + days); this.setState({ expire: d.toISOString(), expirePreset: days }); }}>
                                                    {days} days
                                                </button>
                                            ))
                                        }
                                    </div>
                                    { this.expireInvalid() ? <p className={classes.slmFieldError} role="alert">Choose an expiry date in the future.</p> : null }
                                </div>
                            </div>
                            <div className={"formElement "+classes.spCheckRow}>
                                <FormControlLabel
                                    classes={{label: classes.label}}
                                    style={{alignItems: "flex-start", textAlign: "left", margin: "0px"}}
                                    control={
                                        <Checkbox
                                            tabIndex={0}
                                            onClick={() => this.setState({ requireVerification: !requireVerification })}
                                            checkedIcon={<Check className={classes.checkedIcon} />}
                                            icon={<Check className={classes.uncheckedIcon} />}
                                            classes={{checked: classes.checked, root: classes.checkRoot}}
                                            checked={requireVerification ? true : false}
                                            inputProps={{ "aria-describedby": "slm-verify-help" }}
                                        />
                                    }
                                    label="Require recipients to verify their email"
                                />
                                <p className={classes.spCheckHelp} id="slm-verify-help">
                                    Recipients confirm their address with a one-time code or link before they can open the files.
                                    { validation.result && validation.result.verificationRequired && !requireVerification ? " A File Attachment Rule applies to this file, so verification is turned on when you send." : "" }
                                </p>
                            </div>
                        </section>

                        <section className={classes.spCard} aria-labelledby="slm-message-title">
                            <h3 className={classes.spCardTitle} id="slm-message-title"><ChatBubbleOutline aria-hidden="true" />Message <span className={classes.spLabelMuted}>(optional)</span></h3>
                            <div className="formElement">
                                <div className={classes.editor}>
                                    <RichTextEditor
                                        toolbarConfig={this.getConfig()}
                                        customControls={this.getCustomControls()}
                                        onChange={(content) => this.setState({ message: content })}
                                        value={message}
                                        toolbarClassName="sd-editor-toolbar"
                                        placeholder="Add a message…"
                                    />
                                </div>
                            </div>
                        </section>

                        { this.renderFileSummary() }
                        { context && (context.farEnabled || validation.state === "off") ? this.renderFarCheck() : null }
                        </>
                    }
                </div>
                <div className={classes.slmFooter}>
                    {
                        sent ?
                            <div className={classes.slmButtons}>
                                <button type="button" className={classes.slmSend} onClick={() => this.props.onSuccess()}>Done</button>
                            </div>
                        : confirmClose ?
                            <div className={classes.slmConfirm} role="alertdialog" aria-label="Discard this share?">
                                <span>Discard this share? The recipients and message you entered will be lost.</span>
                                <div className={classes.slmButtons}>
                                    <button type="button" className={classes.slmCancel} onClick={() => this.setState({ confirmClose: false })} autoFocus>Keep editing</button>
                                    <button type="button" className={classes.slmDiscard} onClick={() => this.props.onClose()}>Discard</button>
                                </div>
                            </div>
                        : <>
                            <p className={classes.slmStatus} id="slm-send-status" aria-live="polite">{saving ? "Sending the link…" : disabledReason}</p>
                            <div className={classes.slmButtons}>
                                <button type="button" className={classes.slmSend} onClick={() => this.shareLink()} disabled={disabledReason !== null}
                                    aria-describedby={disabledReason ? "slm-send-status" : undefined}>
                                    { saving ? <LoaderComponent color="white" saving={true} /> :
                                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 3760 3500">
                                            <path d="M970 3489c-52-16-86-45-108-92-19-39-178-874-169-883 8-8 888 629 884 640-3 12-423 297-472 320-43 21-95 26-135 15zm1322-152c-40-15-1104-787-1100-798 2-5 379-378 838-830C3215 542 3575 185 3559 195c-8 5-614 481-1348 1059-734 577-1339 1049-1345 1049-13 0-743-529-790-572-67-61-89-186-47-268 45-90-23-61 1743-738C2675 379 3472 74 3542 48c138-53 167-54 195-11 9 13 16 36 15 51-1 19-1046 2900-1130 3113-18 45-74 101-127 125-56 27-146 31-203 11z"/>
                                        </svg>
                                    }
                                    Send Link
                                </button>
                                <button type="button" className={classes.slmCancel} onClick={this.requestClose} disabled={saving}>Cancel</button>
                            </div>
                        </>
                    }
                </div>
                {
                    farModal && reviewEvaluation && reviewEvaluation.unresolvedPairs.length > 0 ?
                        <FarExceptionModal
                            open={true}
                            evaluation={reviewEvaluation}
                            canOverride={this.state.far.canOverride}
                            isAdmin={!!this.state.far.isAdmin}
                            acknowledgeMode={farModal === "acknowledge"}
                            transferType="email"
                            onClose={() => this.setState({ farModal: null })}
                            onRemoveRecipients={this.onFarRemoveRecipients}
                            onResolve={this.onFarResolve}
                        />
                    : null
                }
                { this.renderRulesDialog() }
                {
                    addressBookModal ?
                        <AddressBookModal open={addressBookModal} onClose={() => this.onAddressBook(false)} onAdd={this.onAddressBookAdd} existingRecipients={recipients}
                            user={this.store ? this.store.getState().user : null} />
                    : null
                }
            </Dialog>
        );
    }
}

ShareLinkModal.defaultProps = {
    open: false,
    downloadLink: null,
    linkPassword: "",
    expire: null,
    requireVerification: false,
    initialRecipients: [],
    items: [],
    path: "",
    history: null
};
ShareLinkModal.propTypes = {
    classes: PropTypes.object.isRequired,
    store: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    open: PropTypes.bool,
    linkHash: PropTypes.string,
    downloadLink: PropTypes.string,
    linkPassword: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    expire: PropTypes.string,
    requireVerification: PropTypes.bool,
    initialRecipients: PropTypes.array,
    /** Selected My Docs items; only used for a thumbnail fallback. File data comes from the server. */
    items: PropTypes.array,
    path: PropTypes.string,
    history: PropTypes.object
};

export default withStyles(theme => ({ ...shareLinkModalStyle(theme), ...sendPageStyle(theme), ...shareLinkLayoutStyle(theme) }))(ShareLinkModal);
